import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { BEIGE_600, GRAY_300, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIInputField } from 'components/common/MUIInputField'
import { ProductSegment } from 'constants/product/productTypes'
import { logger } from 'utils/logging'
import { AddressData, AddressSection } from './AddressSection.component'
import { AreaRangeFields } from './AreaRangeFields.component'
import { missingAddressTextFields } from './usePropertyCompleteness'
import type { NewPropertyData } from 'models/purchaseFlowRevamp'
import { MUIIconButton } from 'components/common/MUIIconButton'
import { PfRevampEventName } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'

interface Props {
  /** Callback to close the create form and return to search mode */
  onClose: () => void
  /** Callback when form is submitted with all required data */
  onSubmit: (data: { name: string; address: string; newPropertyData: NewPropertyData }) => void
  /** Whether the form submission is in flight — disables submit + close */
  isSubmitting?: boolean
  /** Leaves submitting to the caller's own button, through the ref. */
  hideSubmit?: boolean
  /** Reports whether the form can be submitted. */
  onStateChange?: (state: { canSave: boolean }) => void
  /** What the client typed into the property search, carried over as the address query. */
  initialAddressQuery?: string
}

/** Lets the drawer submit the form from its footer button. */
export interface CreatePropertyHandle {
  submit: () => void
}

/**
 * Inline form for creating a new property within the PropertySelectionDrawer.
 * Supports two sub-modes: Google Places autofill and manual address entry.
 */
export const CreatePropertyForm = forwardRef<CreatePropertyHandle, Props>(({ onClose, onSubmit, isSubmitting = false, hideSubmit = false, onStateChange, initialAddressQuery = '' }, ref) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const [isManualMode, setIsManualMode] = useState(false)
  const [propertyName, setPropertyName] = useState('')
  const [useAddressAsName, setUseAddressAsName] = useState(false)
  const [areaRange, setAreaRange] = useState<ProductSegment | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [addressData, setAddressData] = useState<AddressData | null>(null)
  const [isAddressQueryEdited, setIsAddressQueryEdited] = useState(false)
  const handleAddressQueryEdited = useCallback(() => setIsAddressQueryEdited(true), [])

  const hasAddress = !!addressData?.address
  const displayAddress = addressData?.address ?? ''

  useEffect(() => {
    if (!useAddressAsName) return
    if (displayAddress) setPropertyName(displayAddress)
  }, [useAddressAsName, displayAddress])

  const isFormValid = useMemo(() => {
    if (!propertyName) return false

    // The range prices the products: session-create refuses a property with neither a range nor an exact
    // area, so letting the form submit without one just moves the failure somewhere it cannot be fixed inline.
    if (!areaRange) return false

    if (isManualMode) {
      if (!addressData?.isManual) return false
      if (!addressData.country) return false
      return missingAddressTextFields(addressData).length === 0
    }

    // `propertyAddress` being non-null says only that a place was picked, not
    // that Google held every component of it — the absent ones arrive as `''`.
    // Session-create rejects such a property with INCOMPLETE_ADDRESS, so the
    // four required text fields are checked here too.
    if (!addressData || addressData.isManual || !addressData.propertyAddress) return false
    return missingAddressTextFields(addressData.propertyAddress).length === 0
  }, [propertyName, areaRange, isManualMode, addressData])

  const handleSubmit = useCallback(() => {
    setShowErrors(true)
    if (!isFormValid || !addressData) return

    const prefilled = !!initialAddressQuery
    dispatchPfRevampEvent({
      type: PfRevampEventName.PROPERTY_CREATE_SUBMITTED,
      payload: {
        prefilled,
        prefillKept: prefilled && !isAddressQueryEdited && !addressData.isManual,
        manual: addressData.isManual,
        areaRange,
      },
    })

    if (addressData.isManual) {
      onSubmit({
        name: propertyName,
        address: addressData.address,
        newPropertyData: {
          propertyAddress: {
            street: addressData.street,
            streetNumber: addressData.streetNumber,
            city: addressData.city,
            postalCode: addressData.postalCode,
            country: addressData.country,
            regionCode: '',
            coordinates: addressData.coordinates,
          },
          areaRange: areaRange ?? undefined,
          isManualAddress: true,
          manualAddressString: addressData.address,
          manualCoordinates: addressData.coordinates,
        },
      })
      return
    }

    if (!addressData.propertyAddress) {
      logger.warn('Google Place data incomplete — could not extract address', {
        context: { component: 'CreatePropertyForm', action: 'handleSubmit' },
      })
      return
    }

    onSubmit({
      name: propertyName,
      address: addressData.address,
      newPropertyData: {
        propertyAddress: addressData.propertyAddress,
        areaRange: areaRange ?? undefined,
        isManualAddress: false,
      },
    })
  }, [isFormValid, addressData, propertyName, areaRange, onSubmit, initialAddressQuery, isAddressQueryEdited])

  useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit])

  useEffect(() => {
    onStateChange?.({ canSave: isFormValid })
  }, [isFormValid, onStateChange])

  return (
    <Box
      sx={{
        backgroundColor: WHITE,
        border: `1px solid ${BEIGE_600}`,
        borderRadius: '0.8rem',
        boxShadow: '0px 1px 4px rgba(19, 24, 29, 0.06)',
      }}
    >

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          padding: 1.5,
          paddingBottom: 1.2,
          borderBottom: `1px solid ${GRAY_300}`,
        }}
      >

        <Typography variant="text-md" fontWeight="semiBold" color={GRAY_900}>
          {t('purchase_flow_revamp:new_property_title')}
        </Typography>

        <MUIIconButton size="sm" icon={<CloseRoundedIcon />} ariaLabel={t('common:close')} disabled={isSubmitting} onClick={onClose} />

      </Stack>

      <Stack gap={1.6} sx={{ padding: 1.5 }}>

        <AddressSection
          showErrors={showErrors}
          onAddressChange={setAddressData}
          onManualModeChange={setIsManualMode}
          initialQuery={initialAddressQuery}
          onQueryEdited={handleAddressQueryEdited}
        />

        <Stack gap={0.8}>

          <MUIInputField
            label={t('purchase_flow_revamp:property_name_label')}
            placeholder={t('purchase_flow_revamp:property_name_label')}
            value={propertyName}
            disabled={useAddressAsName}
            isError={showErrors && !propertyName && !useAddressAsName}
            onChange={(e) => {
              setPropertyName(e.target.value)
              if (useAddressAsName) setUseAddressAsName(false)
            }}
          />

          <MUICheckbox
            label={
              <Typography variant="text-sm" color={GRAY_900}>
                {t('purchase_flow_revamp:use_address_as_name')}
              </Typography>
            }
            checked={useAddressAsName}
            disabled={!hasAddress}
            onChange={(e) => {
              setUseAddressAsName(e.target.checked)
              if (e.target.checked && displayAddress) {
                setPropertyName(displayAddress)
              }
            }}
          />

        </Stack>

        <AreaRangeFields
          areaRange={areaRange}
          onAreaRangeChange={setAreaRange}
          showErrors={showErrors}
        />

      </Stack>

      {!hideSubmit &&
      <Box sx={(theme) => ({ padding: `0 ${theme.spacing(1.5)} ${theme.spacing(1.5)}`, display: 'flex', justifyContent: 'flex-end' })}>
        <MUIButton
          size="md"
          disabled={!isFormValid || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting
            ? t('purchase_flow_revamp:creating_property')
            : t('purchase_flow_revamp:add_new_property_button')
          }
        </MUIButton>
      </Box>
      }

    </Box>
  )
})
